import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ToggleButton } from '@/components/ui/custom/ToggleButton';
import { AiAgentsExport } from '@/modules/analytics/components/aiAgents/AiAgentsExport';
import { AiAgentsHallucinationTable } from '@/modules/analytics/components/aiAgents/AiAgentsHallucinationTable';
import { AiAgentsInsights } from '@/modules/analytics/components/aiAgents/AiAgentsInsights';
import { AiAgentsInsightsEmptyState } from '@/modules/analytics/components/aiAgents/AiAgentsInsightsEmptyState';
import { CsatScoreAnalysisCard } from '@/modules/analytics/components/aiAgents/CsatScoreAnalysisCard';
import { GoalAchievedCard } from '@/modules/analytics/components/aiAgents/GoalAchievedCard';
import { SelectedAiAgentMetricProvider } from '@/modules/analytics/components/aiAgents/hooks/useSelectedAiAgentMetric';
import { KnowledgeBasePerformanceTable } from '@/modules/analytics/components/aiAgents/KnowledgeBasePerformanceTable';
import { AiAgentCustomInsightsSection } from '@/modules/analytics/components/aiAgents/singleAgentInsights/AiAgentCustomInsightsSection';
import { UserSentimentCard } from '@/modules/analytics/components/aiAgents/UserSentimentCard';
import { VoiceSessions } from '@/modules/analytics/components/aiAgents/VoiceSessions';
import { AccountAnalyticsFilters } from '@/modules/analytics/components/filters/AccountAnalyticsFilters/AccountAnalyticsFilters';
import { AI_AGENTS_ALLOWED_FILTERS } from '@/modules/analytics/constants';
import { useTrackedComparison } from '@/modules/analytics/hooks/useComparisonMode';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

const CARD_MIN_HEIGHT_CLASS = 'min-h-[362px]';

const selectSelectedAiAgentCount = (state: RootState): number =>
  state.analytics.accountAnalytics?.find((filter) => filter.type === 'brainIds')?.brainIds
    ?.length ?? 0;

const AiAgentsContent = () => {
  const { t } = useTranslation();
  useSegments(PageName.ANALYTICS_AI_AGENTS);
  const { isComparing, toggleComparison } = useTrackedComparison(PageName.ANALYTICS_AI_AGENTS);
  const selectedAiAgentCount = useSelector(selectSelectedAiAgentCount);

  return (
    <div className="mx-auto max-w-6xl">
      <AccountAnalyticsFilters
        page={PageName.ANALYTICS_AI_AGENTS}
        allowedFilters={AI_AGENTS_ALLOWED_FILTERS}
        rightActions={
          <ToggleButton pressed={isComparing} onClick={toggleComparison}>
            {t('analytics.compare_prev')}
          </ToggleButton>
        }
        exportActions={<AiAgentsExport />}
      />
      <div className="space-y-6">
        <AiAgentsInsights />
        <VoiceSessions />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <CsatScoreAnalysisCard className={CARD_MIN_HEIGHT_CLASS} />
          <UserSentimentCard className={CARD_MIN_HEIGHT_CLASS} />
          <GoalAchievedCard className={CARD_MIN_HEIGHT_CLASS} />
        </div>
        <KnowledgeBasePerformanceTable />
        <AiAgentsHallucinationTable />
        {selectedAiAgentCount === 0 && <AiAgentsInsightsEmptyState />}
        {selectedAiAgentCount === 1 && <AiAgentCustomInsightsSection />}
      </div>
    </div>
  );
};

export function AiAgents() {
  return (
    <SelectedAiAgentMetricProvider>
      <AiAgentsContent />
    </SelectedAiAgentMetricProvider>
  );
}
